import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table } from 'react-bootstrap';
import { motion } from 'framer-motion';
import * as Papa from 'papaparse';

import vinosCSV from '../data/vinos.csv';

interface Seccion {
  titulo: string;
  subtitulo: string;
  nota?: string;
}

interface Secciones {
  [key: number]: Seccion;
}

const SECCIONES: Secciones = {
  1: {
    titulo: "📞 Asesoramiento personalizado",
    subtitulo: "🚛 Envíos a domicilio en CABA y GBA, precios sin iva"
  },
  2: {
    titulo: "🍇 Nuestra Selección Exclusiva",
    subtitulo: "Vinos elegidos cuidadosamente por nuestro equipo, para quienes buscan excelencia y momentos únicos."
  },
  3: {
    titulo: "🍷 Catalogo Completo",
    subtitulo: "Etiquetas variadas para todos los gustos y presupuestos. Perfectas para descubrir y disfrutar.",
    nota: "👉 Pedí cajas cerradas o armá tu propio combo. ¡Te ayudamos a elegir!"
  },
  4: {
    titulo: "🥂 Espumantes",
    subtitulo: "El toque perfecto para brindar o transformar una reunión en algo especial.",
    nota: "👉 Sumale elegancia y burbujas a tus momentos, con opciones que se adaptan a cada ocasión"
  },
  5: {
    titulo: "🍸 Aperitivos y Espirituosas",
    subtitulo: "Sabores únicos para el inicio de cada encuentro, o para darle un giro a tu noche.",
    nota: "👉 Descubrí vermuts, bitters y destilados premium para elevar tus barras a otro nivel"
  },
  6: {
    titulo: "🗝️ Nuestros Cofres de Tesoros",
    subtitulo: "(Solo a Caja Cerrada) 📦",
    nota: "👉 Llevate vinos exclusivos y etiquetas limitadas en formatos ideales, para sorprender y agasajar"
  },
  7: {
    titulo: "🍻 Varios",
    subtitulo: "Energía, frescura y variedad fuera de lo clásico",
    nota: "👉 Cervezas, bebidas energéticas y demás opciones para complementar tu compra!"
  }
};

interface ParseResult {
  data: Array<{
    Categoria: string;
    Producto: string;
    Tipo: string;
    'Botellas por Caja': string;
    Minorista: string;
    Mayorista: string;
    'Minorista (Por Caja)': string;
    'Mayorista (Por Caja)': string;
  }>;
  errors: Array<any>;
  meta: any;
}

interface Vino {
  categoria: number;
  Producto: string;
  Tipo: string;
  'Botellas por Caja': number;
  Minorista: number;
  Mayorista: number;
  'Minorista (Por Caja)': number;
  'Mayorista (Por Caja)': number;
}

const ListaPrecios = () => {
  const [vinos, setVinos] = useState<Vino[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    Papa.parse(vinosCSV, {
      download: true,
      header: true,
      complete: (results: ParseResult) => {
        const vinosFormateados = results.data
          .filter(vino => vino.Categoria && vino.Producto)
          .map(vino => ({
            categoria: parseInt(vino.Categoria),
            Producto: vino.Producto,
            Tipo: vino.Tipo,
            'Botellas por Caja': parseInt(vino['Botellas por Caja']),
            Minorista: parseFloat(vino.Minorista.replace('$', '').replace('.', '').replace(',', '.')),
            Mayorista: parseFloat(vino.Mayorista.replace('$', '').replace('.', '').replace(',', '.')),
            'Minorista (Por Caja)': parseFloat(vino['Minorista (Por Caja)'].replace('$', '').replace('.', '').replace(',', '.')),
            'Mayorista (Por Caja)': parseFloat(vino['Mayorista (Por Caja)'].replace('$', '').replace('.', '').replace(',', '.')),
          }));

        setVinos(vinosFormateados);
        setLoading(false);
      },
      error: (error: Error) => {
        console.error('Error al cargar el CSV:', error);
        setLoading(false);
      }
    });
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const handleClickPedidoPrimerBoton = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', 'PedidoDesdePrimerBoton');
    }
  };

    const handleClickPedidoUltimoBoton = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', 'PedidoDesdeUltimoBoton');
    }
  };

  const renderSeccion = (categoria: number) => {
    const seccion = SECCIONES[categoria];
    const vinosSeccion = vinos
      .filter(vino => vino.categoria === categoria)
      .filter(vino => vino.Producto.toLowerCase().includes(busqueda.toLowerCase()));  

    if (vinosSeccion.length === 0) return null;

    return (
      <motion.div key={categoria} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="mb-5">
        <div className="text-center mb-4">
          <h2 className="h2 brand-font mb-2">{seccion.titulo}</h2>
          <p className="medium mb-2">{seccion.subtitulo}</p>
          {seccion.nota && <p className="medium mb-4">{seccion.nota}</p>}
        </div>
        <div className="table-responsive">
          <Table className="price-table">
            <thead>
              <tr>
                <th colSpan={2}></th>
                <th colSpan={2} className="text-center align-middle">Minorista</th>
                <th colSpan={2} className="text-center align-middle">Mayorista</th>
              </tr>
              <tr>
                <th>Producto</th>
                <th>Botellas por Caja</th>
                <th>Precio Unitario</th>
                <th>Precio por Caja</th>
                <th>Precio Unitario</th>
                <th>Precio por Caja</th>
              </tr>
            </thead>
            <tbody>
              {vinosSeccion.map((vino, index) => (
                <tr key={index}>
                  <td>{vino.Producto}</td>
                  <td className="text-center">{vino['Botellas por Caja']}</td>
                  <td>{formatPrice(vino.Minorista)}</td>
                  <td>{formatPrice(vino['Minorista (Por Caja)'])}</td>
                  <td>{formatPrice(vino.Mayorista)}</td>
                  <td>{formatPrice(vino['Mayorista (Por Caja)'])}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p className="lead">Cargando lista de precios...</p>
      </Container>
    );
  }  

  return (
    <Container fluid="xxl" className="py-5">
      <Row className="justify-content-center">
        <Col xs={12} md={11} lg={10} className="text-center mb-1">
          <h2 className="display-4 brand-font">¿Qué tenés que saber para hacer tu compra?</h2>
        </Col>
      </Row>
  
      <Row className="justify-content-center">
        <Col xs={12} md={11} lg={10}>
          <div className="faq-container mb-5">
            <div className="faq-content">
              <div className="faq-section mb-4">
                <h4 className="mb-3">🛒 ¿Cuál es el mínimo de compra?</h4>
                <p>6 unidades (pueden ser mixtas).</p>
              </div>
              <div className="faq-section mb-4">
                <h4 className="mb-3">🛒 ¿Cómo puedo acceder a precios mayoristas?</h4>
                <p>Para acceder a precios mayoristas la compra debe superar los $700.000</p>
              </div>
              <div className="faq-section mb-4">
                <h4 className="mb-3">📦 ¿Hacen envíos?</h4>
                <p>Sí, realizamos envíos a todo el país:</p>
                <ul className="mb-3">
                  <li className="mb-2">
                    <strong>📍 CABA y GBA:</strong>
                    <ul>
                      <li>Envío gratis para compras minoristas a partir de $150.000.</li>
                      <li>Envío gratis para compras mayoristas sin mínimo adicional.</li>
                    </ul>
                  </li>
                  <br></br>
                  <li>
                    <strong>🚚 Interior del país:</strong>
                    <ul>
                      <li><strong>Minoristas: </strong> Enviamos por Andreani. El costo corre por cuenta del cliente.</li>
                      <br></br>
                      <li><strong>Mayoristas: </strong> Despachamos sin costo desde nuestro depósito al expreso que el cliente elija. El costo del expreso corre por cuenta y elección del cliente.</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="faq-section mb-4">
                <h4 className="mb-3">⏱️ ¿Cuáles son los tiempos de entrega?</h4>
                <p>Apuntamos a entregar dentro de las 72 horas hábiles desde la confirmación del pago. Como estamos con gran demanda, nosotros te vamos a ir avisando por donde se encuentra tu pedido para que te quedes tranquilo o si hay una eventual demora</p>
              </div>
              <div className="faq-section mb-4">
                <h4 className="mb-3">🏬 ¿Se puede retirar el pedido?</h4>
                <p>Trabajamos únicamente online con envíos puerta a puerta en todo el país, con el objetivo de poder ofrecerte la mayor variedad de precios y productos del mercado!</p>
              </div>
              <div className="faq-section">
                <h4 className="mb-3">💸 ¿Qué medios de pago aceptan?</h4>
                <p>Todos los pedidos se abonan por transferencia o depósito previo únicamente.</p>
              </div>
            </div>
          </div>
        </Col>
      </Row>
  
      <Row className="justify-content-center">
        <Col md={10} lg={9} className="text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="display-4 mb-2 brand-font"
          >
            ¿Cómo hacer tu compra?
          </motion.h1>
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="h3 mb-5 brand-font"
            style={{ fontWeight: 500 }}
          >
            <em>en solo (tres) pasos</em>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-5 mt-4"
          >
            <p className="lead mb-4">
              1. Elegí tus vinos favoritos de la lista que está un poco más abajo en esta página.
            </p>
            <p className="lead mb-4">
              2. Nos hablás al WhatsApp así podemos <strong> asesorarte personalmente </strong> (hacé click en el botón verde).
            </p>
            <p className="lead mb-4">
              3. ¡Coordinamos el envío puerta a puerta para que lleguen directo a tu casa!
            </p>
            <motion.a
              href="https://api.whatsapp.com/message/5LUL6QCX55ICO1?autoload=1&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-button"
              onClick={handleClickPedidoPrimerBoton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fab fa-whatsapp me-2"></i>
              Hablar por WhatsApp
            </motion.a>
          </motion.div>
          <div className="separator mb-4"></div>
        </Col>
      </Row>
  
      <Row className="justify-content-center">
        <Col xs={12} md={11} lg={10} className="text-center mb-3">
          <h1 className="display-4 brand-font">Lista de Precios</h1>
        </Col>
      </Row>
  
      <Row className="justify-content-center mb-5">
        <Col xs={12} md={7} lg={6}>
          <input
            type="text"
            className="form-control"
            placeholder="Filtrá por producto, cepa o bodega!"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </Col>
      </Row>

      {[1, 2, 3, 4, 5, 6, 7].map((categoria) => (
        <Row className="justify-content-center mt-3" key={categoria}>
          <Col xs={12} md={11} lg={10}>
            {renderSeccion(categoria)}
          </Col>
        </Row>
      ))}

      <Row className="justify-content-center">
        <Col xs={9} md={8} lg={7} className="text-center">
          <motion.a
            href="https://api.whatsapp.com/message/5LUL6QCX55ICO1?autoload=1&app_absent=0"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-button"
            onClick={handleClickPedidoUltimoBoton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <i className="fab fa-whatsapp me-2"></i>
            Hace tu pedido acá!
          </motion.a>
          </Col>
      </Row>
    </Container>
  );
}

export default ListaPrecios;